import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Trash2,
  MapPin, ExternalLink, Package, X, Calculator,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  DESIGN_COAT_PRODUCTS,
  PIGMENTS_PRODUCTS,
  TOP_COAT_PRODUCTS,
} from '@/components/proepoxy/productData';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import { packLabel } from '@/components/proepoxy/utils';
import PrioritySelector from '@/components/PrioritySelector';

const comfortAvailability = (packs) => {
  const parts = packs.map((p) => (p.kg ? `${p.kg}kg (${p.size}m²)` : `${p.size}m²`));
  return packs.length === 1 ? `Available as a ${parts[0]} unit` : `Available in ${parts.join(', ')} packs`;
};

export default function MetallicStep4({ state, priority, setPriority, currentStep, goToStep, navigate, onExit }) {
  const {
    primerAreas, setPrimerAreas, designCoatSelections, pigmentSelections, topCoatSelections,
    steps,
    addPrimerArea, removePrimerArea, updatePrimerArea, updateAreaPackQty,
    updateAreaField, updateAreaFloorPrepQty,
    initAreaTopCoat, updateAreaTopCoatFinish, updateAreaTopCoatPackQty,
    getAreaTopCoatSubtotal, getAreaCoverage, getAreaSubtotal,
    initializeDesignCoatForArea, updateDesignCoatProduct,
    updateDesignCoatPackQtyLegacy, updateCrystalUnitsLegacy,
    getDesignCoatSubtotal, getDesignCoatCoverage,
    selectTopCoatProduct, updateTopCoatFinishLegacy, updateTopCoatPackQtyLegacy,
    getTopCoatSubtotal, getTopCoatCoverage,
    addMetallicPowder, updateMetallicPowderQty,
    addPigmentPaste, removePigmentPaste, updatePigmentPastePackQty,
    addGlitter, removeGlitter, updateGlitterPackQty,
    addSpray, updateSprayQty, getPigmentsSubtotal,
    calculateTotal, getItemCount,
  } = state;
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-[#27292E]">Design Coat</h2>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calculator className="w-4 h-4" />
                      Areas from Step 1
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Select your design coat product for each area. Your m² values have been carried forward.
                  </p>

                  {primerAreas.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                      <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 mb-4">No areas defined. Please go back to Step 1 to add areas.</p>
                      <Button
                        onClick={() => goToStep(1)}
                        variant="outline"
                        className="border-2 border-[#7A0B7A] text-[#7A0B7A]"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Define Areas
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {primerAreas.map((area, index) => {
                        // Design-coat selection is auto-initialised by useMetallicOrder
                        // when currentStep becomes 4 (see hook's Step 4 useEffect).
                        // We just read whatever is in the store, falling back to a
                        // neutral default so the first render doesn't crash.
                        const selection = designCoatSelections[area.id] || { productType: 'comfort', packs: [], crystalUnits: 0 };
                        const m2Value = Number(area.m2) || 0;
                        
                        return (
                          <div
                            key={area.id}
                            className="border-2 border-gray-200 rounded-xl p-4 hover:border-[#7A0B7A]/30 transition-colors"
                            data-testid={`design-coat-area-${index}`}
                          >
                            {/* Area Header */}
                            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                              <div>
                                <h3 className="font-bold text-[#27292E]">{area.name}</h3>
                                <p className="text-sm text-gray-500">{m2Value}m² • {area.color} {area.primerType === '1_part' ? '1-Part' : '2-Part'} Primer</p>
                              </div>
                              <span className="bg-[#7A0B7A]/10 text-[#7A0B7A] px-3 py-1 rounded-full text-sm font-medium">
                                {m2Value}m²
                              </span>
                            </div>

                            {/* Product Type Selection */}
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Select Design Coat</label>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <button
                                  onClick={() => updateDesignCoatProduct(area.id, 'comfort', area.m2)}
                                  className={`p-4 rounded-xl text-left transition-all border-2 ${
                                    selection.productType === 'comfort'
                                      ? 'border-[#7A0B7A] bg-[#7A0B7A]/5'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                  data-testid={`area-${index}-comfort`}
                                >
                                  <h4 className="font-medium text-[#27292E]">{DESIGN_COAT_PRODUCTS.comfort.name}</h4>
                                  <p className="text-xs text-gray-500 mt-1">{comfortAvailability(DESIGN_COAT_PRODUCTS.comfort.packs)}</p>
                                </button>
                                <button
                                  onClick={() => updateDesignCoatProduct(area.id, 'crystal', area.m2)}
                                  className={`p-4 rounded-xl text-left transition-all border-2 ${
                                    selection.productType === 'crystal'
                                      ? 'border-[#7A0B7A] bg-[#7A0B7A]/5'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                  data-testid={`area-${index}-crystal`}
                                >
                                  <h4 className="font-medium text-[#27292E]">{DESIGN_COAT_PRODUCTS.crystal.name}</h4>
                                  <p className="text-xs text-gray-500 mt-1">7.5kg units • 5-7m² coverage each</p>
                                </button>
                              </div>
                            </div>

                            {/* Comfort Epoxy Packs */}
                            {selection.productType === 'comfort' && (
                              <div className="bg-gray-50 rounded-xl p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-medium text-[#27292E]">Recommended Packs</h4>
                                  <span className="text-xs text-gray-500">Adjust quantities if needed</span>
                                </div>
                                <div className="space-y-2">
                                  {DESIGN_COAT_PRODUCTS.comfort.packs.map(packOption => {
                                    const areaPack = selection.packs.find(p => p.size === packOption.size);
                                    const qty = areaPack ? areaPack.quantity : 0;
                                    return (
                                      <div
                                        key={packOption.size}
                                        className={`p-4 rounded-lg ${
                                          qty > 0 ? 'bg-white border-2 border-[#7A0B7A]/20' : 'bg-white/50'
                                        }`}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="font-medium text-[#27292E]">{packLabel(packOption)}</span>
                                          <div className="flex items-center gap-2">
                                            <button
                                              onClick={() => updateDesignCoatPackQtyLegacy(area.id, packOption.size, -1)}
                                              className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/10 transition-colors"
                                              data-testid={`design-${index}-pack-${packOption.size}-minus`}
                                            >
                                              <Minus className="w-4 h-4" />
                                            </button>
                                            <span className="w-8 text-center font-bold text-lg">{qty}</span>
                                            <button
                                              onClick={() => updateDesignCoatPackQtyLegacy(area.id, packOption.size, 1)}
                                              className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white hover:bg-[#5a085a] transition-colors"
                                              data-testid={`design-${index}-pack-${packOption.size}-plus`}
                                            >
                                              <Plus className="w-4 h-4" />
                                            </button>
                                          </div>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                          <span className="text-gray-500">£{packOption.price.toFixed(2)} each</span>
                                          {qty > 0 && (
                                            <span className="font-bold text-[#7A0B7A]">
                                              Total: £{(qty * packOption.price).toFixed(2)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                {/* Coverage info */}
                                <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm">
                                  <span className="text-gray-600">Coverage: {getDesignCoatCoverage(area.id)}m²</span>
                                  <span className="font-bold text-[#7A0B7A]">Subtotal: £{getDesignCoatSubtotal(area.id).toFixed(2)}</span>
                                </div>
                              </div>
                            )}

                            {/* Crystal Epoxy Units */}
                            {selection.productType === 'crystal' && (
                              <div className="bg-gray-50 rounded-xl p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-medium text-[#27292E]">7.5kg Units Required</h4>
                                  <span className="text-xs text-gray-500">Each covers 5-7m²</span>
                                </div>
                                <div className={`p-4 rounded-lg bg-white ${selection.crystalUnits > 0 ? 'border-2 border-[#7A0B7A]/20' : ''}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium text-[#27292E]">7.5kg unit</span>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => updateCrystalUnitsLegacy(area.id, -1)}
                                        className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/10 transition-colors"
                                        data-testid={`design-${index}-crystal-minus`}
                                      >
                                        <Minus className="w-4 h-4" />
                                      </button>
                                      <span className="w-8 text-center font-bold text-lg">{selection.crystalUnits}</span>
                                      <button
                                        onClick={() => updateCrystalUnitsLegacy(area.id, 1)}
                                        className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white hover:bg-[#5a085a] transition-colors"
                                        data-testid={`design-${index}-crystal-plus`}
                                      >
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">£{DESIGN_COAT_PRODUCTS.crystal.price.toFixed(2)} each</span>
                                    {selection.crystalUnits > 0 && (
                                      <span className="font-bold text-[#7A0B7A]">
                                        Total: £{(selection.crystalUnits * DESIGN_COAT_PRODUCTS.crystal.price).toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {/* Coverage info */}
                                <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm">
                                  <span className="text-gray-600">Coverage: {getDesignCoatCoverage(area.id)}m²</span>
                                  <span className="font-bold text-[#7A0B7A]">Subtotal: £{getDesignCoatSubtotal(area.id).toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Running Total */}
                <div className="bg-[#7A0B7A] text-white rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">Order Total</p>
                      <p className="text-xs text-white/60">{getItemCount()} item{getItemCount() !== 1 ? 's' : ''} • Excl. VAT</p>
                    </div>
                    <p className="text-3xl font-bold">
                      £{calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => goToStep(3)}
                    variant="outline"
                    className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(5)}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
  );
}
